import React, {
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import Text from '@/components/ui/text';
import type { InteractiveSize, ValidationState } from '@/theme/size-tokens';
import { validationTextClasses } from '@/theme/size-tokens';

import FormGridRow from './form-grid-row';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Column definition for FormGrid fields
 */
export type FormGridColumn = {
  /** Unique key identifying this column in row data */
  key: string;
  /** Display label for the column header */
  label: string;
  /** Type of form control to render */
  type: 'input' | 'select' | 'checkbox' | 'textarea' | 'custom';
  /** HTML input type (only for type='input') */
  inputType?: 'text' | 'email' | 'number' | 'tel' | 'url' | 'password';
  /** Placeholder text for input/textarea/select */
  placeholder?: string;
  /** Options for select type */
  options?: { value: string; label: string }[];
  /** Custom render function for type='custom' */
  render?: (
    value: unknown,
    row: FormGridRowData,
    onChange: (value: unknown) => void,
    rowIndex: number
  ) => ReactNode;
  /** Width of the field (Tailwind width class or 'flex-1') */
  width?: string;
  /** Whether this field is required */
  required?: boolean;
  /** Default value for new rows */
  defaultValue?: unknown;
};

/**
 * Row data structure with recursive sub-rows
 */
export type FormGridRowData = {
  /** Unique identifier for the row */
  id: string;
  /** Key-value map of field values */
  values: Record<string, unknown>;
  /** Nested sub-rows */
  subRows?: FormGridRowData[];
};

/**
 * Methods exposed via ref for uncontrolled usage
 */
export type FormGridRef = {
  /** Get all row data including sub-rows */
  getData: () => FormGridRowData[];
  /** Add a new row at root level or as sub-row of parentId */
  addRow: (parentId?: string) => void;
  /** Remove a row by id (searches recursively) */
  removeRow: (id: string) => void;
  /** Validate all rows, returns true if valid */
  validate: () => boolean;
  /** Reset to initial state with one empty row */
  reset: () => void;
};

/**
 * Props for the FormGrid component
 */
export type FormGridProps = {
  /** Column definitions describing each field */
  columns: FormGridColumn[];
  /** Initial rows data (defaults to one empty row) */
  defaultRows?: FormGridRowData[];
  /** Callback when data changes */
  onChange?: (rows: FormGridRowData[]) => void;
  /** Size variant for form controls */
  size?: InteractiveSize;
  /** Maximum nesting depth for sub-rows (default: 3) */
  maxDepth?: number;
  /** Label for add sub-row button */
  addSubRowLabel?: string;
  /** Whether to allow sub-rows (manual add button) */
  allowSubRows?: boolean;
  /** Callback to determine if a row should automatically have sub-rows. When provided, sub-rows are auto-managed based on row data (e.g., type === 'object'). No manual add button is shown. */
  autoSubRow?: (row: FormGridRowData) => boolean;
  /** Whether the entire grid is disabled */
  disabled?: boolean;
  /** Whether to show column headers */
  showHeaders?: boolean;
  /** Custom class name */
  className?: string;
  /** Validation state map by row id and column key */
  validationStates?: Record<string, Record<string, ValidationState>>;
  /** Label displayed above the grid */
  label?: string;
  /** Helper text displayed below the grid */
  helperText?: string;
  /** Error message displayed below the grid (overrides helperText) */
  error?: string;
  /** Shows required asterisk next to label */
  required?: boolean;
};

// =============================================================================
// HELPERS
// =============================================================================

const MOBILE_BREAKPOINT = 640;

function getCheckboxColumnWidth(label: string): string {
  const minCh = 8;
  return `${Math.max(minCh, label.length + 2)}ch`;
}

/**
 * Convert a Tailwind width utility class to a valid CSS grid track value.
 * e.g. 'w-24' → '6rem', 'flex-1' → 'minmax(0, 1fr)', 'w-1/2' → '50%'
 */
function tailwindWidthToCss(width: string): string {
  if (width === 'flex-1' || width === 'w-full') return 'minmax(0, 1fr)';
  if (width === 'w-auto') return 'auto';
  // w-{number} → n * 0.25rem
  const numMatch = width.match(/^w-(\d+)$/);
  if (numMatch) return `${parseInt(numMatch[1]) * 0.25}rem`;
  // w-{num}/{den} fraction
  const fracMatch = width.match(/^w-(\d+)\/(\d+)$/);
  if (fracMatch)
    return `${((parseInt(fracMatch[1]) / parseInt(fracMatch[2])) * 100).toFixed(2)}%`;
  // Already a valid CSS value (contains a unit or keyword)
  if (
    /\d+(px|rem|em|%|fr|ch|vw|vh)/.test(width) ||
    /^(auto|min-content|max-content|fit-content)$/.test(width)
  ) {
    return width;
  }
  return 'auto';
}

function getDesktopGridTemplate(columns: FormGridColumn[]): string {
  const columnTracks = columns.map((col) => {
    if (col.width) {
      return tailwindWidthToCss(col.width);
    }
    if (col.type === 'checkbox') {
      return getCheckboxColumnWidth(col.label);
    }
    return 'minmax(0, 1fr)';
  });

  return `${columnTracks.join(' ')} 4rem`;
}

/** Generate a unique ID for rows */
function generateId(): string {
  return crypto.randomUUID();
}

/** Create an empty row with default values from columns */
function createEmptyRow(columns: FormGridColumn[]): FormGridRowData {
  const values: Record<string, unknown> = {};
  columns.forEach((col) => {
    if (col.defaultValue !== undefined) {
      values[col.key] = col.defaultValue;
    } else if (col.type === 'checkbox') {
      values[col.key] = false;
    } else {
      values[col.key] = '';
    }
  });
  return { id: generateId(), values };
}

/** Recursively find and update a row */
function updateRowInTree(
  rows: FormGridRowData[],
  targetId: string,
  updater: (row: FormGridRowData) => FormGridRowData
): FormGridRowData[] {
  return rows.map((row) => {
    if (row.id === targetId) {
      return updater(row);
    }
    if (row.subRows && row.subRows.length > 0) {
      return {
        ...row,
        subRows: updateRowInTree(row.subRows, targetId, updater),
      };
    }
    return row;
  });
}

/** Recursively remove a row */
function removeRowFromTree(
  rows: FormGridRowData[],
  targetId: string
): FormGridRowData[] {
  return rows
    .filter((row) => row.id !== targetId)
    .map((row) => ({
      ...row,
      subRows: row.subRows
        ? removeRowFromTree(row.subRows, targetId)
        : undefined,
    }));
}

/** Recursively add a sub-row to a parent */
function addSubRowToParent(
  rows: FormGridRowData[],
  parentId: string,
  newRow: FormGridRowData
): FormGridRowData[] {
  return rows.map((row) => {
    if (row.id === parentId) {
      return { ...row, subRows: [...(row.subRows ?? []), newRow] };
    }
    if (row.subRows && row.subRows.length > 0) {
      return {
        ...row,
        subRows: addSubRowToParent(row.subRows, parentId, newRow),
      };
    }
    return row;
  });
}

/** Check if any required fields are empty */
function validateRows(
  rows: FormGridRowData[],
  columns: FormGridColumn[]
): boolean {
  const requiredKeys = columns.filter((c) => c.required).map((c) => c.key);

  const checkRow = (row: FormGridRowData): boolean => {
    for (const key of requiredKeys) {
      const value = row.values[key];
      if (value === undefined || value === null || value === '') {
        return false;
      }
    }
    if (row.subRows) {
      return row.subRows.every(checkRow);
    }
    return true;
  };

  return rows.every(checkRow);
}

/** Check whether a row has any user-entered content (non-empty, non-default values) */
export function rowHasContent(row: FormGridRowData): boolean {
  return Object.values(row.values).some(
    (v) => v !== '' && v !== false && v !== null && v !== undefined
  );
}

/**
 * After a value change, ensure there is always a trailing empty row at every level
 * that contains the changed row.
 */
function ensureTrailingEmptyRows(
  rows: FormGridRowData[],
  changedRowId: string,
  columns: FormGridColumn[]
): FormGridRowData[] {
  // Check root level
  const rootIdx = rows.findIndex((r) => r.id === changedRowId);
  if (rootIdx === rows.length - 1) {
    if (rowHasContent(rows[rootIdx])) {
      return [...rows, createEmptyRow(columns)];
    }
    return rows;
  }

  // Recurse into sub-rows
  return rows.map((row) => {
    if (!row.subRows || row.subRows.length === 0) return row;
    const subIdx = row.subRows.findIndex((r) => r.id === changedRowId);
    if (subIdx === row.subRows.length - 1) {
      if (rowHasContent(row.subRows[subIdx])) {
        return { ...row, subRows: [...row.subRows, createEmptyRow(columns)] };
      }
      return row;
    }
    // Recurse deeper
    const newSubRows = ensureTrailingEmptyRows(
      row.subRows,
      changedRowId,
      columns
    );
    return newSubRows !== row.subRows ? { ...row, subRows: newSubRows } : row;
  });
}

/** Get the depth of a row in the tree (0-indexed) */
function getRowDepth(
  rows: FormGridRowData[],
  targetId: string,
  currentDepth: number = 0
): number | null {
  for (const row of rows) {
    if (row.id === targetId) {
      return currentDepth;
    }
    if (row.subRows) {
      const foundDepth = getRowDepth(row.subRows, targetId, currentDepth + 1);
      if (foundDepth !== null) {
        return foundDepth;
      }
    }
  }
  return null;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * FormGrid - Dynamic form builder with support for adding/removing rows and nested sub-rows.
 *
 * The component manages its own state internally and exposes methods via ref for
 * uncontrolled usage. Use `onChange` callback to react to data changes.
 *
 * @example
 * ```tsx
 * const formGridRef = useRef<FormGridRef>(null);
 *
 * const columns: FormGridColumn[] = [
 *   { key: 'name', label: 'Field Name', type: 'input', placeholder: 'Enter name' },
 *   { key: 'type', label: 'Type', type: 'select', options: [
 *     { value: 'string', label: 'String' },
 *     { value: 'number', label: 'Number' },
 *   ]},
 *   { key: 'required', label: 'Required', type: 'checkbox' },
 * ];
 *
 * <FormGrid ref={formGridRef} columns={columns} onChange={console.log} />
 *
 * // Get data via ref
 * const data = formGridRef.current?.getData();
 * ```
 */
const FormGrid = React.forwardRef<FormGridRef, FormGridProps>(
  (
    {
      columns,
      defaultRows,
      onChange,
      size = 'md',
      maxDepth = 3,
      addSubRowLabel = 'Add Sub-Row',
      allowSubRows = true,
      autoSubRow,
      disabled = false,
      showHeaders = true,
      className = '',
      validationStates = {},
      label,
      helperText,
      error,
      required = false,
    },
    ref
  ) => {
    // Internal state
    const [rows, setRows] = useState<FormGridRowData[]>(() => {
      if (defaultRows && defaultRows.length > 0) {
        return defaultRows;
      }
      return [createEmptyRow(columns)];
    });

    const [isMobile, setIsMobile] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const desktopGridTemplate = getDesktopGridTemplate(columns);

    // Responsive detection via ResizeObserver
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const updateLayout = () => {
        const containerWidth = container.offsetWidth;
        setIsMobile(containerWidth < MOBILE_BREAKPOINT);
      };

      updateLayout();

      const resizeObserver = new ResizeObserver(updateLayout);
      resizeObserver.observe(container);

      return () => resizeObserver.disconnect();
    }, []);

    // Notify parent of changes
    useEffect(() => {
      onChange?.(rows);
    }, [rows, onChange]);

    // Row management handlers
    const handleAddRow = useCallback(() => {
      setRows((prev) => [...prev, createEmptyRow(columns)]);
    }, [columns]);

    const handleAddSubRow = useCallback(
      (parentId: string) => {
        setRows((prev) =>
          addSubRowToParent(prev, parentId, createEmptyRow(columns))
        );
      },
      [columns]
    );

    const handleRemoveRow = useCallback((id: string) => {
      setRows((prev) => {
        const result = removeRowFromTree(prev, id);
        // Ensure at least one row remains
        if (result.length === 0) {
          return prev;
        }
        return result;
      });
    }, []);

    const handleValueChange = useCallback(
      (rowId: string, key: string, value: unknown) => {
        setRows((prev) => {
          // First update the value
          let updated = updateRowInTree(prev, rowId, (row) => ({
            ...row,
            values: { ...row.values, [key]: value },
          }));

          // If autoSubRow is provided, check if we need to add/remove sub-rows
          if (autoSubRow) {
            // Get the row's current depth to respect maxDepth
            const rowDepth = getRowDepth(updated, rowId);

            updated = updateRowInTree(updated, rowId, (row) => {
              const shouldHaveSubRows = autoSubRow(row);
              const hasSubRows = row.subRows && row.subRows.length > 0;
              const canAddMore = rowDepth !== null && rowDepth < maxDepth;

              if (shouldHaveSubRows && !hasSubRows && canAddMore) {
                // Auto-add a sub-row
                return { ...row, subRows: [createEmptyRow(columns)] };
              } else if (!shouldHaveSubRows && hasSubRows) {
                // Remove sub-rows when condition no longer applies
                return { ...row, subRows: undefined };
              }
              return row;
            });
          }

          // Always ensure there is a trailing empty row at every level
          updated = ensureTrailingEmptyRows(updated, rowId, columns);

          return updated;
        });
      },
      [autoSubRow, columns, maxDepth]
    );

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        getData: () => rows,
        addRow: (parentId?: string) => {
          if (parentId) {
            handleAddSubRow(parentId);
          } else {
            handleAddRow();
          }
        },
        removeRow: (id: string) => handleRemoveRow(id),
        validate: () => validateRows(rows, columns),
        reset: () => setRows([createEmptyRow(columns)]),
      }),
      [rows, columns, handleAddRow, handleAddSubRow, handleRemoveRow]
    );

    return (
      <div ref={containerRef} className={`w-full ${className}`}>
        {/* Label */}
        {label && (
          <Text
            as="label"
            size="sm"
            weight="semibold"
            className="mb-1.5 block"
            required={required}
          >
            {label}
          </Text>
        )}

        {/* Column Headers (desktop only) */}
        {showHeaders && !isMobile && (
          <div
            className="mt-3 mb-1 grid items-center gap-3"
            style={{ gridTemplateColumns: desktopGridTemplate }}
          >
            {columns.map((col) => (
              <Text
                key={col.key}
                size="sm"
                align={col.type === 'checkbox' ? 'center' : 'left'}
                className="text-muted-foreground min-w-0"
                required={col.required}
              >
                {col.label}
              </Text>
            ))}
            {/* Spacer for action buttons */}
            <div className="w-16 shrink-0" />
          </div>
        )}

        {/* Rows */}
        <div className="space-y-1">
          {rows.map((row) => (
            <FormGridRow
              key={row.id}
              row={row}
              columns={columns}
              depth={0}
              maxDepth={maxDepth}
              isMobile={isMobile}
              size={size}
              allowSubRows={allowSubRows && !autoSubRow}
              autoSubRow={autoSubRow}
              disabled={disabled}
              addSubRowLabel={addSubRowLabel}
              onValueChange={handleValueChange}
              onAddSubRow={handleAddSubRow}
              onRemove={handleRemoveRow}
              canRemove={rowHasContent(row) && rows.length > 1}
              validationStates={validationStates[row.id]}
            />
          ))}
        </div>

        {/* Helper / error text */}
        {(helperText ?? error) && (
          <Text
            size="xs"
            className={`mt-1.5 ${error ? validationTextClasses.error : 'text-muted-foreground'}`}
          >
            {error ?? helperText}
          </Text>
        )}
      </div>
    );
  }
);

FormGrid.displayName = 'FormGrid';

export default FormGrid;
